import { useContext, useEffect } from "react";
import { Typography, Icon, Link, Box } from "@mui/material";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useTheme } from "@emotion/react";
import { AuthContext } from "src/utils/AuthContext.js";
import { UpgradeConsumerButton } from "../upgrades/consumer/UpgradeConsumerButton.js";
import DownloadSampleButton from "./DownloadSampleButton.js";
import { getPageRoute } from "src/services/Config.js";
import InstructionsLayout from "./InstructionsLayout.js";
import { Helmet } from "react-helmet";
import { customEvent } from "src/utils/gtag.js";

const UpgradeTrigger = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const forwardUrl =
    searchParams.get("forward") || getPageRoute("home", "HOME");
  const upgradeType = searchParams.get("upgradeType") || "STANZA_PLUS_CALENDAR";
  const location = useLocation();
  const { calendar = null } = location.state || {};
  const handle = calendar?.handle;
  const calendarId = calendar?._id;

  // Define content based on upgrade type
  const pageContent = {
    CALENDAR_LIMIT: {
      title: "Calendar Limit Reached",
      description: "You've hit the limit of 2 free calendars.",
      heading: (
        <>
          You've hit your limit of{" "}
          <span style={{ fontWeight: "bold" }}>2 free calendars.</span>
        </>
      ),
      learnMoreUrl:
        "https://stanza-1.gitbook.io/stanza-help-center/for-subscribers/stanzaplus/benefits",
    },
    STANZA_PLUS_CALENDAR: {
      title: "Stanza+ Exclusive Calendar",
      description:
        "This premium calendar is exclusive to Stanza+ members. Upgrade today to access this and other premium calendars.",
      heading: (
        <>
          This calendar is only available to{" "}
          <span style={{ fontWeight: "bold" }}>Stanza+ members.</span>
        </>
      ),
      learnMoreUrl:
        "https://spotlight.stanzacal.com/what-makes-a-calendar-exclusive-on-stanza/",
    },
  }[upgradeType];

  // Add page load tracking
  useEffect(() => {
    if (user) {
      customEvent({
        name: `upgrade_trigger_${upgradeType.toLowerCase()}`,
        category: "Upgrade",
        label: `Landed on ${
          upgradeType === "STANZA_PLUS_CALENDAR"
            ? "Stanza+ Calendar"
            : "Calendar Limit"
        } Page`,
        data: {
          user_id: user._id,
          user_email: user.email,
          user_handle: user.handle ?? "no_handle",
          calendar_id: calendarId,
          calendar_handle: handle,
          upgrade_type: upgradeType,
        },
      });
    }
  }, [Boolean(user), calendarId, handle, upgradeType]);

  useEffect(() => {
    if (user?.hasValidSubscription) {
      // If user already has a subscription, redirect them back to where they came from
      navigate(forwardUrl);
    }

    // Scroll to top on load
    window.scrollTo({
      top: 0,
      behavior: "auto",
    });
  }, [user, navigate, forwardUrl]);

  return (
    <>
      <Helmet>
        <title>{pageContent.title}</title>
        <meta name="description" content={pageContent.description} />
      </Helmet>

      <InstructionsLayout>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "left",
            margin: "80px auto 0",
          }}
        >
          <Icon
            baseClassName="material-symbols-outlined"
            sx={{
              display: "block",
              margin: "0",
              marginLeft: "0",
              marginRight: "auto",
              color: `${theme.palette.icon.primary}`,
              verticalAlign: "middle",
              fontSize: "2rem",
              mb: 1,
              fontWeight: 300,
              textAlign: "left",
              width: "100%",
            }}
          >
            lock
          </Icon>

          <Typography variant="h3" sx={{ mb: 1, textAlign: "left" }}>
            {pageContent.heading}
          </Typography>

          <UpgradeConsumerButton
            sx={{
              mt: 3.5,
              width: "100%",
              py: 1.5,
              borderRadius: 50,
              fontWeight: 500,
              textTransform: "none",
            }}
            state={{ calendar, source: `upgrade_trigger_${upgradeType.toLowerCase()}` }}
          >
            BECOME A MEMBER TODAY
          </UpgradeConsumerButton>

          <DownloadSampleButton
            calendar={calendar}
            sx={{
              mt: 2,
              mb: 2,
            }}
          />

          <Link
            href={pageContent.learnMoreUrl}
            target="_blank"
            rel="noopener noreferrer"
            variant="body2"
            sx={{
              textDecoration: "underline",
              color: "text.secondary",
              mt: 2,
              mb: 4,
            }}
          >
            Learn more
          </Link>
        </Box>
      </InstructionsLayout>
    </>
  );
};

export default UpgradeTrigger;
